<template>
  <div
    :class="[
      $style.base,
      {
        [$style.isExpanded]: isExpanded,
        [$style.disabled]: disabled,
        [$style.standalone]: standalone
      }
    ]"
    v-test="'select-employee'"
  >
    <IconSpinner v-if="loading" />
    <div v-else-if="hasEmployees" ref="el" :class="$style.inner">
      <Employee
        v-if="!standalone"
        :employee="selectedEmployee"
        @click="toggleList"
        v-test="'employee-dropdown'"
      />
      <div :class="$style.list" v-show="isExpanded || standalone">
        <div :class="$style.listInner">
          <Employee
            v-if="
              (standalone || selectedEmployee) && !employeeSelectionRequired
            "
            v-bind="{ standalone }"
            @click="onEmployeeClick(null)"
            v-test="'employee'"
          />
          <Employee
            v-for="(employee, index) in employees"
            :key="index"
            :employee="employee"
            v-bind="{ standalone }"
            @click="onEmployeeClick(employee.id)"
            v-test="'employee'"
          />
        </div>
      </div>
      <svg
        v-if="!standalone"
        :class="$style.arrow"
        width="10"
        height="6"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M.236.67l.47-.449A.855.855 0 011.276 0c.214 0 .42.084.565.221l3.048 2.91L7.936.22A.827.827 0 018.501 0c.213 0 .42.084.57.221l.464.45c.15.143.239.34.239.544a.735.735 0 01-.239.539L5.453 5.651a.806.806 0 01-.564.228.832.832 0 01-.571-.228L.236 1.754a.753.753 0 01-.232-.539c0-.203.087-.4.232-.545z"
          fill="#393939"
          fill-opacity=".5"
        />
      </svg>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, inject, ref, watch, watchEffect } from 'vue';

import IconSpinner from '@/components/IconSpinner.vue';
import { useBookingStore } from '@/stores/booking';
import { useCompanyStore } from '@/stores/company';
import { useEmployeesStore } from '@/stores/employees';
import widgetSettings from '@/widget-settings';

import Employee from './Employee.vue';

defineOptions({ name: 'SelectEmployee' });

const props = defineProps<{
  standalone?: boolean;
}>();

const emit = defineEmits(['submit']);

const gtag = inject<any>('gtag');

const { employeeSelectionRequired } = useCompanyStore();
const { fetchEmployees } = useEmployeesStore();
const { loading } = storeToRefs(useEmployeesStore());
const { employeeId, employee: selectedEmployee } = storeToRefs(
  useBookingStore()
);

const employees = computed(() => {
  // Filter out the selected employee from the list
  const { employees } = useEmployeesStore();
  return employees.filter((e) => e.id !== employeeId.value);
});

fetchEmployees();

const isExpanded = ref(false);
const disabled = computed(() => {
  const { employees } = useEmployeesStore();
  return (
    !props.standalone &&
    employees.length === 1 &&
    (employeeSelectionRequired || widgetSettings.widgetId)
  );
});

const toggleList = () => {
  isExpanded.value = !isExpanded.value;
};

const el = ref(null);
onClickOutside(el, (event) => (isExpanded.value = false));

const selectEmployee = (employeeId: number | null) => {
  const bookingStore = useBookingStore();
  bookingStore.employeeId = employeeId;

  if (props.standalone) {
    emit('submit');
  } else {
    isExpanded.value = false;
  }
};

const onEmployeeClick = (employeeId: number | null) => {
  selectEmployee(employeeId);

  if (employeeId) {
    gtag.trackEvent('employee_select', {
      employee_id: employeeId
    });
  } else {
    gtag.trackEvent('employee_clear');
  }
};

watch(disabled, () => {
  if (disabled.value) {
    selectEmployee(employees.value[0].id);
  }
});

watchEffect(() => {
  const { employees } = useEmployeesStore();
  if (!loading.value && employees.length === 1) {
    selectEmployee(employees[0].id);
  } else if (!loading.value && employees.length === 0) {
    selectEmployee(null);
  }
});

const hasEmployees = computed(() => {
  const { employees } = useEmployeesStore();
  return !loading.value && !!employees.length;
});
</script>

<style lang="scss" module>
.base {
  &:not(.standalone) {
    padding: 0 $spacing;
    margin: $spacing 0;
  }

  &.disabled {
    pointer-events: none;
  }
}

.inner {
  position: relative;
  margin-bottom: $spacing;
  z-index: 3;

  .base:not(.isExpanded):not(.standalone) & {
    border-radius: $border-radius;
    @include box-shadow;
  }
}
.list {
  .base:not(.standalone) & {
    position: absolute;
    width: 100%;
    top: 0;
    left: 0;
    padding-top: $dropdown-item-height;
    border-radius: $border-radius;
    @include box-shadow;
  }
}

.listHeading {
  display: flex;
  justify-content: space-between;
  background-color: white;
  padding: ($spacing * 2) $spacing 0;
  opacity: 1;
}

.listHeader {
  text-transform: uppercase;
  font-size: 15px;
  line-height: 21px;
  @include font-sub;
}

.listInner {
  .base:not(.standalone) & {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    max-height: ($dropdown-item-height + 1) * 6;
  }
}

.arrow {
  position: absolute;
  top: 50%;
  right: $spacing;
  margin-top: -3px;
  z-index: 1;
  pointer-events: none;

  .base.isExpanded & {
    transform: rotate(180deg);
  }

  .base.disabled & {
    display: none;
  }
}
</style>
